import {
    AlertListTypeEnum,
    AlertStatusTypeEnum,
    AlertSubTypes,
    AlertTypes,
    MyAlertListSearchStatusMapping,
} from '../../../utilities/Constants';

import { AppliedFiltersSection } from '../../../components/shared/filters/AppliedFiltersSection';
import { AppliedFiltersView } from '../../../components/shared/filters/AppliedFiltersView';
import { FilterBadge } from '../../../components/shared/filters/FilterBadge';
import { IAlertSearchModel } from '../../../interfaces/INote';
import { IMultiSelectOption } from '../../../interfaces/ILookup';
import { ISelectedFiltersDetails } from '../../../components/shared/filters/AppliedFiltersDetailsBody';
import { useAlertListFilters } from './AlertListFiltersContext';
import { useState } from 'react';

export interface IAlertListAppliedFiltersProps {
    alertListType: AlertListTypeEnum;
    onApplyFilters: (searchModel: IAlertSearchModel) => void;
    onToggleFilterPane: () => void;
    searchModel: IAlertSearchModel;
}

const availableTypeOptions: IMultiSelectOption[] = [
    {
        id: AlertTypes.Act.Value,
        label: AlertTypes.Act.Name,
        value: AlertTypes.Act.Name,
    },
    {
        id: AlertTypes.Watch.Value,
        label: AlertTypes.Watch.Name,
        value: AlertTypes.Watch.Name,
    },
];

const availableSubTypeOptions: IMultiSelectOption[] = [
    {
        id: AlertSubTypes.Collab.Value,
        label: AlertSubTypes.Collab.Name,
        value: AlertSubTypes.Collab.Name,
    },
    {
        id: AlertSubTypes.Chat.Value,
        label: AlertSubTypes.Chat.Name,
        value: AlertSubTypes.Chat.Name,
    },
    {
        id: AlertSubTypes.Review.Value,
        label: AlertSubTypes.Review.Name,
        value: AlertSubTypes.Review.Name,
    },
];

export function AlertListAppliedFilters(props: IAlertListAppliedFiltersProps) {
    const {
        alertOriginTypesLookup,
        alertStatusTypesLookup,
        noteCategoryTypesLookup,
        notePurposeTypesLookup,
        zoneUsersLookup,
        getAlertOriginTypeOptions,
        getAlertStatusTypeOptions,
        getNoteCategoryTypeOptions,
        getNotePurposeTypeOptions,
        getNoteSubCategoryTypeOptions,
        getSortedAvailableUserOptions,
    } = useAlertListFilters();

    const isMyAlertList = props.alertListType === AlertListTypeEnum.MyAlertList;
    const isZoneAlertList = props.alertListType === AlertListTypeEnum.ZoneAlertList;

    const getSelectedAlertStatusOptions = () => {
        if (isMyAlertList) {
            return (props.searchModel.statuses
                ?.map((status) =>
                    MyAlertListSearchStatusMapping.find(
                        (x) => x.alertStatusType === status.status && x.read === status.read
                    )
                )
                .filter((mapping) => mapping !== undefined)
                .map((mapping) =>
                    getAlertStatusTypeOptions(props.alertListType).find(
                        (opt) => opt.id === mapping?.id
                    )
                ) ?? []) as IMultiSelectOption[];
        } else if (isZoneAlertList) {
            return props.searchModel.statuses
                ?.map((status) =>
                    getAlertStatusTypeOptions(props.alertListType).find(
                        (x) => x.id === status.status
                    )
                )
                .filter((lu) => lu !== undefined);
        }

        return [];
    };

    const handleRemoveOrigin = (originTypeId: number) => {
        const updatedOriginTypes =
            props.searchModel.originTypes?.filter((id) => id !== originTypeId) ?? [];
        const updatedSearchModel = { ...props.searchModel, originTypes: updatedOriginTypes };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveMyAlertStatus = (statusToRemove: {
        status: AlertStatusTypeEnum;
        read: boolean;
    }) => {
        const updatedStatuses =
            props.searchModel.statuses?.filter(
                (status) =>
                    !(
                        status.status === statusToRemove.status &&
                        status.read === statusToRemove.read
                    )
            ) ?? [];
        const updatedSearchModel = { ...props.searchModel, statuses: updatedStatuses };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveZoneAlertStatus = (statusIdToRemove: AlertStatusTypeEnum) => {
        const updatedStatuses =
            props.searchModel.statuses?.filter((status) => !(status.status === statusIdToRemove)) ??
            [];
        const updatedSearchModel = { ...props.searchModel, statuses: updatedStatuses };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveUser = (userGuid: string) => {
        const updatedUserGuids =
            props.searchModel.alertUserGuids?.filter((guid) => guid !== userGuid) ?? [];
        const updatedSearchModel = { ...props.searchModel, alertUserGuids: updatedUserGuids };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveAlertType = (alertTypeId: number) => {
        const updatedAlertTypes =
            props.searchModel.alertTypeIds?.filter((id) => id !== alertTypeId) ?? [];
        const updatedSearchModel = { ...props.searchModel, alertTypeIds: updatedAlertTypes };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveAlertSubType = (alertSubTypeId: number) => {
        const updatedAlertSubTypes =
            props.searchModel.alertSubTypeIds?.filter((id) => id !== alertSubTypeId) ?? [];
        const updatedSearchModel = { ...props.searchModel, alertSubTypeIds: updatedAlertSubTypes };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveCategory = (categoryId: number) => {
        const updatedCategories =
            props.searchModel.noteCategories?.filter((id) => id !== categoryId) ?? [];
        const updatedSearchModel = { ...props.searchModel, noteCategories: updatedCategories };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveSubCategory = (categoryId: number) => {
        const updatedSubCategories =
            props.searchModel.noteSubCategories?.filter((id) => id !== categoryId) ?? [];
        const updatedSearchModel = {
            ...props.searchModel,
            noteSubCategories: updatedSubCategories,
        };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemovePurpose = (purposeId: number) => {
        const updatedPurposes = props.searchModel.purpose?.filter((id) => id !== purposeId) ?? [];
        const updatedSearchModel = { ...props.searchModel, purpose: updatedPurposes };
        props.onApplyFilters(updatedSearchModel);
    };

    const sortedOriginSelections = props.searchModel.originTypes
        ?.map((originTypeId) => ({
            value: originTypeId,
            label:
                getAlertOriginTypeOptions(props.alertListType).find(
                    (option) => option.id === originTypeId
                )?.label ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const sortedSelectedMyAlertStatuses = isMyAlertList
        ? getSelectedAlertStatusOptions()
              ?.map((item) => MyAlertListSearchStatusMapping.find((x) => x.id === item.id) ?? null)
              .filter((status) => status)
              .map((status) => ({
                  id: status!.id,
                  label: status!.label,
                  status: status!.alertStatusType,
                  read: status!.read,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
        : [];

    const sortedSelectedZoneAlertStatuses = isZoneAlertList
        ? getSelectedAlertStatusOptions()?.sort((a, b) =>
              (a!.label ?? '').localeCompare(b!.label ?? '')
          )
        : [];

    const sortedSelectedAlertUsers = props.searchModel.alertUserGuids
        ?.map((userGuid) => {
            return getSortedAvailableUserOptions().find((userOpt) => userOpt.guid === userGuid);
        })
        .filter((user) => user !== undefined)
        .sort((a, b) => (a!.label ?? '').localeCompare(b!.label ?? ''));

    const sortedAlertTypes = props.searchModel.alertTypeIds
        ?.map((alertTypeId) => {
            return availableTypeOptions.find((opt) => opt.id === alertTypeId);
        })
        .filter((type) => type !== undefined)
        .sort((a, b) => (a!.label ?? '').localeCompare(b!.label ?? ''));

    const sortedSubAlertTypes = props.searchModel.alertSubTypeIds
        ?.map((alertSubTypeId) => {
            return availableSubTypeOptions.find((opt) => opt.id === alertSubTypeId);
        })
        .filter((type) => type !== undefined)
        .sort((a, b) => (a!.label ?? '').localeCompare(b!.label ?? ''));

    const sortedNoteCategories = props.searchModel.noteCategories
        ?.map((categoryId) => {
            return getNoteCategoryTypeOptions(props.searchModel.originTypes ?? []).find(
                (opt) => opt.id === categoryId
            );
        })
        .filter((category) => category !== undefined)
        .sort((a, b) => (a!.label ?? '').localeCompare(b!.label ?? ''));

    const sortedNoteSubCategories = props.searchModel.noteSubCategories
        ?.map((subCategoryId) => {
            return getNoteSubCategoryTypeOptions(props.searchModel.noteCategories ?? []).find(
                (opt) => opt.id === subCategoryId
            );
        })
        .filter((subCategory) => subCategory !== undefined)
        .sort((a, b) => (a!.label ?? '').localeCompare(b!.label ?? ''));

    const sortedPurposes = props.searchModel.purpose
        ?.map((purposeId) => {
            return getNotePurposeTypeOptions().find((opt) => opt.id === purposeId);
        })
        .filter((purpose) => purpose !== undefined)
        .sort((a, b) => (a!.label ?? '').localeCompare(b!.label ?? ''));

    const getSelectedFiltersDetails = (): ISelectedFiltersDetails[] => {
        const details: ISelectedFiltersDetails[] = [];

        if ((sortedOriginSelections?.length ?? 0) > 0) {
            details.push({
                label: 'Types',
                values: sortedOriginSelections?.map((origin) => origin.label) ?? [],
            });
        }

        if ((sortedSelectedMyAlertStatuses?.length ?? 0) > 0) {
            details.push({
                label: 'Statuses',
                values: sortedSelectedMyAlertStatuses?.map((origin) => origin.label) ?? [],
            });
        }

        if ((sortedSelectedZoneAlertStatuses?.length ?? 0) > 0) {
            details.push({
                label: 'Statuses',
                values: sortedSelectedZoneAlertStatuses?.map((origin) => origin.label) ?? [],
            });
        }

        if ((sortedSelectedAlertUsers?.length ?? 0) > 0) {
            details.push({
                label: 'Users',
                values: sortedSelectedAlertUsers?.map((origin) => origin.label) ?? [],
            });
        }

        if ((sortedAlertTypes?.length ?? 0) > 0) {
            details.push({
                label: 'Act/Watch',
                values: sortedAlertTypes?.map((origin) => origin.label) ?? [],
            });
        }

        if ((sortedSubAlertTypes?.length ?? 0) > 0) {
            details.push({
                label: 'Act Types',
                values: sortedSubAlertTypes?.map((origin) => origin.label) ?? [],
            });
        }

        return details;
    };

    const details = getSelectedFiltersDetails();

    return (
        <AppliedFiltersSection onToggleFilterPane={props.onToggleFilterPane}>
            <AppliedFiltersView selectedFilterDetails={details}>
                {sortedOriginSelections?.map((origin) => (
                    <FilterBadge
                        key={'origin-type-filter-' + origin.value}
                        label={origin.label}
                        onRemove={() => handleRemoveOrigin(origin.value)}
                        isLoading={alertOriginTypesLookup.isFetching}
                        disabled={sortedOriginSelections.length === 1}
                    />
                ))}

                {isMyAlertList &&
                    sortedSelectedMyAlertStatuses?.map((status) => (
                        <FilterBadge
                            key={'my-alert-status-filter-' + status.id}
                            label={status.label}
                            onRemove={() =>
                                handleRemoveMyAlertStatus({
                                    status: status.status,
                                    read: status.read,
                                })
                            }
                            isLoading={alertStatusTypesLookup.isFetching}
                            disabled={sortedSelectedMyAlertStatuses.length === 1}
                        />
                    ))}

                {isZoneAlertList &&
                    sortedSelectedZoneAlertStatuses?.map((status) => (
                        <FilterBadge
                            key={'zone-alert-status-filter-' + status.id}
                            label={status.label}
                            onRemove={() => handleRemoveZoneAlertStatus(status.id)}
                            isLoading={alertStatusTypesLookup.isFetching}
                        />
                    ))}

                {sortedSelectedAlertUsers?.map((user) => (
                    <FilterBadge
                        key={'user-filter-' + user?.guid}
                        label={user.label}
                        onRemove={() => handleRemoveUser(user?.guid ?? '')}
                        isLoading={zoneUsersLookup.isFetching}
                    />
                ))}

                {sortedAlertTypes?.map((alertType) => (
                    <FilterBadge
                        key={'alert-type-filter-' + alertType}
                        label={alertType.label}
                        onRemove={() => handleRemoveAlertType(alertType.id)}
                    />
                ))}

                {sortedSubAlertTypes?.map((alertSubType) => (
                    <FilterBadge
                        key={'alert-subtype-filter-' + alertSubType}
                        label={alertSubType.label}
                        onRemove={() => handleRemoveAlertSubType(alertSubType.id)}
                    />
                ))}

                {sortedNoteCategories?.map((category) => (
                    <FilterBadge
                        key={'category-filter-' + category?.id}
                        label={category.label}
                        onRemove={() => handleRemoveCategory(category?.id)}
                        isLoading={noteCategoryTypesLookup.isFetching}
                    />
                ))}

                {sortedNoteSubCategories?.map((subCategory) => (
                    <FilterBadge
                        key={'subcategory-filter-' + subCategory.id}
                        label={subCategory.label}
                        onRemove={() => handleRemoveSubCategory(subCategory.id)}
                        isLoading={noteCategoryTypesLookup.isFetching}
                    />
                ))}

                {sortedPurposes?.map((purpose) => (
                    <FilterBadge
                        key={'purpose-filter-' + purpose}
                        label={purpose.label}
                        onRemove={() => handleRemovePurpose(purpose.id)}
                        isLoading={notePurposeTypesLookup.isFetching}
                    />
                ))}
            </AppliedFiltersView>
        </AppliedFiltersSection>
    );
}
